import { useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Zap,
  Building2,
  Users,
  CreditCard,
  Shield,
  ShieldCheck,
  LogOut,
  ArrowLeft,
  Plug,
  Megaphone,
  Monitor,
  ScrollText,
  Globe,
  Layout,
  BarChart3,
  MessageSquare,
  Bot,
  Search,
  BookOpen,
  BookText,
  Languages,
  Newspaper,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const menuItems = [
  { title: "Dashboard", url: "/admin", icon: LayoutDashboard },
  { title: "Empresas", url: "/admin/tenants", icon: Building2 },
  { title: "Relatórios", url: "/admin/reports", icon: BarChart3 },
  { title: "Impacto", url: "/admin/impact", icon: Zap },
  { title: "Usuários", url: "/admin/users", icon: Users },
  { title: "Planos", url: "/admin/plans", icon: CreditCard },
  { title: "Templates", url: "/admin/templates", icon: Layout },
  { title: "Comunicação", url: "/admin/comunicacao", icon: Megaphone },
  { title: "Login Banners", url: "/admin/login-banners", icon: Monitor },
  { title: "Landing Page", url: "/admin/landing-page", icon: Globe },
  { title: "Integrações", url: "/admin/integracoes", icon: Plug },
  { title: "Aceites Legais", url: "/admin/legal", icon: Shield },
  { title: "WhatsApp Hub", url: "/admin/whatsapp", icon: MessageSquare },
  { title: "Projetos IA", url: "/admin/projetos", icon: Bot },
  { title: "Prospecção BDR", url: "/admin/prospeccao", icon: Search },
  { title: "Auditoria", url: "/admin/auditoria", icon: ScrollText },
  { title: "Decisões Arquiteturais", url: "/admin/adrs", icon: BookOpen },
  { title: "Editor de Ajuda", url: "/admin/ajuda", icon: BookText },
  { title: "Traduções de UI", url: "/admin/traducoes", icon: Languages },
  { title: "Changelog", url: "/admin/changelog", icon: Newspaper },
  { title: "Segurança", url: "/admin/seguranca", icon: ShieldCheck },
];

export function AdminSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const { profile, signOut } = useAuth();
  const isCollapsed = state === "collapsed";

  const isActive = (url: string) => {
    if (url === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(url);
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <Link to="/admin" className="flex items-center gap-3">
          <img src="/brand/Solux-v2-1200x500.png" alt="Solux CRM" className="h-9 w-auto shrink-0" />
          {!isCollapsed && (
            <div className="flex flex-col">
              <span className="font-bold text-sidebar-foreground">Super Admin</span>
              <span className="text-xs text-sidebar-foreground/60">Painel de controle</span>
            </div>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Gestão</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    tooltip={item.title}
                  >
                    <Link to={item.url}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        <div className={cn("flex flex-col gap-2", isCollapsed && "items-center")}>
          <Button variant="outline" size="sm" asChild className="w-full">
            <Link to="/app">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {!isCollapsed && "Voltar ao App"}
            </Link>
          </Button>
          {!isCollapsed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={signOut}
              className="w-full text-muted-foreground"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </Button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
